"use client";

import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useQueryClient } from "@tanstack/react-query";
import { Dialog, ResponsiveDialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/app/_module/components/ui/dialog";
import { Button } from "@/app/_module/components/ui/button";
import { Label } from "@/app/_module/components/ui/label";
import { Switch } from "@/app/_module/components/ui/switch";
import { Textarea } from "@/app/_module/components/ui/textarea";
import { Badge } from "@/app/_module/components/ui/badge";
import { UserPlus, Mail, DollarSign } from "lucide-react";
import { useToast } from "@/app/_module/hooks/use-toast";
import api from "@/app/_module/services/api";
import { getApiUrl } from "@/app/_module/services/config";
import type { Agency } from "../../types/types";
import agencyQueryKeys from "../../services/keys";
import { isDemoMode } from "@/app/_module/utils/demoMode";

const inviteSchema = z.object({
  emails: z.string().min(1, "Please enter at least one email address"),
  agencyPays: z.boolean(),
  customMessage: z.string().optional(),
});

type InviteFormData = z.infer<typeof inviteSchema>;

interface InviteUsersModalProps {
  agency: Agency;
  trigger?: React.ReactNode;
}


export const InviteUsersModal = function({ agency, trigger }: InviteUsersModalProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [emailList, setEmailList] = useState<string[]>([]);

  const form = useForm<InviteFormData>({
    resolver: zodResolver(inviteSchema),
    defaultValues: {
      emails: "",
      agencyPays: false,
      customMessage: "",
    },
  });

  const { handleSubmit, reset, watch, setValue } = form;
  const agencyPays = watch("agencyPays");

  const parseEmails = (emailString: string): string[] => {
    return emailString
      .split(/[,\n]/)
      .map(email => email.trim())
      .filter(email => email.length > 0);
  };

  const handleEmailChange = (value: string) => {
    setValue("emails", value);
    const emails = parseEmails(value);
    setEmailList(emails);
  };

  const onSubmit = async (data: InviteFormData) => {
    // DEMO SANDBOX: an invite sends a REAL email to a REAL address.
    //
    // Unlike most gates here this one reaches outside the sandbox entirely — a visitor
    // could use a pooled seat to mail a stranger an invitation into a demo agency, and
    // accepting it would attach a real person to a workspace that gets wiped on reset.
    if (isDemoMode()) {
      window.dispatchEvent(
        new CustomEvent('demo-restricted', {
          detail:
            "Inviting agents is disabled in the demo sandbox. Start a free trial to invite your real team.",
        }),
      );
      return;
    }

    setIsLoading(true);
    try {
      const emails = parseEmails(data.emails);

      // Real API call to send agency invites
      // Invited users are always agents - manager role is set later in agency dashboard
      const response = await api.post({
        url: getApiUrl(`/agencies/${agency.id}/invite-agents`),
        body: {
          emails,
          role: "agent",
          agencyPays: data.agencyPays,
          message: data.customMessage,
        },
        auth: true,
      });

      // Handle response - axios interceptor unwraps response.data, so response IS the data
      const responseData = response as any;
      const statusCode = responseData?.statusCode;

      // Success if statusCode is 200 or 201
      if (statusCode === 200 || statusCode === 201) {
        // Invalidate agency users query to refresh pending invites list in real-time
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: agencyQueryKeys.agencyUsers(agency.id) }),
          queryClient.invalidateQueries({
            predicate: (query) => {
              const key = query.queryKey;
              return Array.isArray(key) && key[0] === 'agencies' && key[1] === 'agencyUsers';
            }
          }),
        ]);

        toast({
          title: "Invites Sent",
          description: `Successfully sent ${emails.length} invitation${emails.length > 1 ? 's' : ''} to join ${agency.name}.`,
        });

        reset();
        setEmailList([]);
        setOpen(false);
      } else {
        // Include error message from response if available
        const errorMessage = responseData?.message || 'Failed to send invitations';
        throw new Error(errorMessage);
      }
    } catch (error: unknown) {
      let errorMessage = "Failed to send invitations. Please try again.";

      // Extract more detailed error information if available
      if (error instanceof Error && (error as any).response) {
        const responseData = (error as any).response.data;
        if (responseData && responseData.message) {
          errorMessage = responseData.message;

          // Handle specific error cases
          if (responseData.message.includes('SendGrid')) {
            errorMessage = "Email service error: " + responseData.message;
          } else if (responseData.message.includes('Invalid email')) {
            errorMessage = "Invalid email address: " + responseData.message;
          } else if (responseData.message.includes('permission')) {
            errorMessage = "Permission error: " + responseData.message;
          } else if (responseData.message.includes('not configured')) {
            errorMessage = "Email service not configured: " + responseData.message;
          }
        }
      } else if (error instanceof Error && error.message) {
        // Handle network or other errors
        if (error.message.includes('network')) {
          errorMessage = "Network error. Please check your internet connection.";
        } else if (error.message.includes('timeout')) {
          errorMessage = "Request timed out. Please try again.";
        } else if (error.message.includes('Failed to send invitations')) {
          errorMessage = "Invitation service error. Please try again or contact support.";
        }
      }

      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });

      // Log the full error for debugging
      console.error("Invitation error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (!newOpen) {
      reset();
      setEmailList([]);
    }
  };

  return (
    <Dialog onOpenChange={handleOpenChange} open={open}>
      <DialogTrigger asChild>
        {trigger || (
          <Button className="bg-primary hover:bg-primary/90">
            <UserPlus className="h-4 w-4 mr-2" />
            Invite Agent
          </Button>
        )}
      </DialogTrigger>
      <ResponsiveDialogContent size="2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5" />
            Invite Agents to {agency.name}
          </DialogTitle>
          <DialogDescription>
            Send invitations to agents to join your agency. They'll receive an email with signup instructions.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Email Addresses */}
          <div className="space-y-2">
            <Label htmlFor="emails">Email Addresses</Label>
            <Textarea
              id="emails"
              placeholder="Enter email addresses separated by commas or new lines&#10;user1@example.com, user2@example.com&#10;user3@example.com"
              value={form.watch("emails")}
              onChange={(e) => handleEmailChange(e.target.value)}
              rows={4}
              className="resize-none"
            />
            {emailList.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">
                  {emailList.length} email{emailList.length > 1 ? 's' : ''} will be invited:
                </p>
                <div className="flex flex-wrap gap-2">
                  {emailList.map((email, index) => (
                    <Badge key={index} variant="secondary" className="text-xs">
                      {email}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Payment Method */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <Label htmlFor="agency-pays" className="text-base font-medium">
                  Agency pays for this seat
                </Label>
                <p className="text-sm text-muted-foreground">
                  {agencyPays
                    ? "Your agency will be charged for this user's subscription"
                    : "User will pay for their own subscription"
                  }
                </p>
              </div>
              <Switch
                id="agency-pays"
                checked={agencyPays}
                onCheckedChange={(checked) => setValue("agencyPays", checked)}
              />
            </div>

            {agencyPays && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <DollarSign className="h-5 w-5 text-green-600 mt-0.5" />
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-green-900">
                      Agency Payment Selected
                    </p>
                    <p className="text-sm text-green-700">
                      The agent will be added to your agency&apos;s monthly billing.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Custom Message */}
          <div className="space-y-2">
            <Label htmlFor="customMessage">Custom Message (Optional)</Label>
            <Textarea
              id="customMessage"
              placeholder="Add a personal message to include in the invitation email..."
              value={form.watch("customMessage")}
              onChange={(e) => setValue("customMessage", e.target.value)}
              rows={3}
              className="resize-none"
            />
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isLoading || emailList.length === 0}
              className="bg-primary hover:bg-primary/90"
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                  Sending Invites...
                </>
              ) : (
                <>
                  <Mail className="h-4 w-4 mr-2" />
                  Send {emailList.length} Invitation{emailList.length > 1 ? 's' : ''}
                </>
              )}
            </Button>
          </div>
        </form>
      </ResponsiveDialogContent>
    </Dialog>
  );
};
